import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { Input } from 'primitives/input'
import { cn } from 'util/cn'

import { ApiErrorMessage } from '../../../controls/errors/api_error_message'
import usePreviousProps from '../../../hooks/previous_props_hook'
import { useAxios } from '../../../hooks/use_axios'
import { usePersistedState } from '../../../hooks/use_persisted_state'
import { SpeakeasyLogo } from '../../../images'
import { updateLiveChatAccount } from '../../../reduxes/slices/accounts/actions'
import { makeGetContact } from '../../../reduxes/slices/contacts/selectors'
import { getCurrentHandle } from '../../../reduxes/slices/current/selectors'
import liteApiRoutes from '../../../routes/lite_api_routes.json'
import { TimelineContent } from '../timeline_content'

export const NameAndEmailForm = ({ ...props }) => {
  const dispatch = useDispatch()
  const axios = useAxios()
  const currentHandle = useSelector(getCurrentHandle)
  const getContact = useMemo(makeGetContact, [])
  const contact = useSelector((state) =>
    getContact(state, { handle: currentHandle }),
  )
  const [notifyState, setNotifyState] = usePersistedState<boolean | null>(
    'notifyWidgetUser',
    null,
  )
  const [formStep, setFormStep] = useState(1)
  const previousFormStep = usePreviousProps(formStep)

  const { name, email } = contact || {}

  useEffect(() => {
    let nextStep = 1
    if (name) {
      if (email) {
        if (notifyState === false || notifyState === true) {
          nextStep = 4
        } else {
          nextStep = 3
        }
      } else {
        nextStep = 2
      }
    }
    if (previousFormStep) {
      setTimeout(() => setFormStep(nextStep), 500)
    } else {
      setFormStep(nextStep)
    }
  }, [email, name, notifyState, previousFormStep])

  const submit = ({ name, email, notify }: any) => {
    dispatch(axios(updateLiveChatAccount({ name, email, notify }))).then(() => {
      if (notify === false || notify === true) {
        setNotifyState(notify)
      }
    })
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    const formData = new FormData(event.target)
    const name = formData.get('name')
    const email = formData.get('email')
    submit({ name, email })
  }

  return (
    <Stack
      as={'form'}
      onSubmit={handleSubmit}
      css={{ button: { padding: '$1 $3' } }}
      {...props}
      className={cn('timeline', props.className)}
    >
      <TimelineContent
        avatar={
          <div className={avatarStyles()}>
            <SpeakeasyLogo />
          </div>
        }
        timelineInfo={
          <div className={'timeline-info'}>
            <b className={'timeline-info__handle'}>Speakeasy Bot</b>
          </div>
        }
        className={cn(containerStyles())}
      >
        {name ? (
          <span>
            👋 Before we get started what should we call you?{' '}
            <span className={blueTextStyles()}>
              <b>{name}</b>
              <i className={cn('fa fa-check', checkIconStyles())} />
            </span>
          </span>
        ) : (
          <>
            <label htmlFor="set_widget_account_name">
              What should we call you?
            </label>
            <Input
              id={'set_widget_account_name'}
              name={'name'}
              placeholder={'Name'}
              action={
                <Button type={'submit'} className={blueButtonStyles()}>
                  Go
                </Button>
              }
            />
          </>
        )}
      </TimelineContent>
      {formStep >= 2 && (
        <TimelineContent
          avatar={
            <div className={avatarStyles()}>
              <SpeakeasyLogo />
            </div>
          }
          timelineInfo={
            <div className={'timeline-info'}>
              <b className={'timeline-info__handle'}>Speakeasy Bot</b>
            </div>
          }
          className={cn(containerStyles())}
        >
          {email ? (
            <span>
              Thanks <b>{name}</b>, what is your email?{' '}
              <span className={blueTextStyles()}>
                <b>{email}</b>
                <i className={cn('fa fa-check', checkIconStyles())} />
              </span>
            </span>
          ) : (
            <>
              <label htmlFor="set_widget_account_email">
                <span>
                  Thanks <b>{name}</b>, what is your email?
                </span>
              </label>
              <Input
                id={'set_widget_account_email'}
                name={'email'}
                placeholder={'email@address.com'}
                action={
                  <Button type={'submit'} className={blueButtonStyles()}>
                    Go
                  </Button>
                }
              />
            </>
          )}
        </TimelineContent>
      )}
      {formStep >= 3 && (
        <TimelineContent
          avatar={
            <div className={avatarStyles()}>
              <SpeakeasyLogo />
            </div>
          }
          timelineInfo={
            <div className={'timeline-info'}>
              <b className={'timeline-info__handle'}>Speakeasy Bot</b>
            </div>
          }
          className={cn(containerStyles())}
        >
          {notifyState === null ? (
            <Stack gap={2}>
              <span>
                Send notifications for new messages to <b>{email}</b>?
              </span>
              <Stack
                direction={'row'}
                gap={2}
                css={{ '>div': { display: 'flex' }, label: { padding: '$3' } }}
              >
                <Button onClick={() => submit({ notify: false })}>No</Button>
                <Button onClick={() => submit({ notify: true })}>
                  Yes, send email notifications
                </Button>
              </Stack>
            </Stack>
          ) : (
            <span>
              Send notifications for new messages to <b>{email}</b>?{' '}
              <span className={blueTextStyles()}>
                <b>{notifyState ? 'Yes, send email notifications' : 'No'}</b>
              </span>
              <i className={cn('fa fa-check', checkIconStyles())} />
            </span>
          )}
        </TimelineContent>
      )}
      {formStep >= 4 && (
        <TimelineContent
          avatar={
            <div className={avatarStyles()}>
              <SpeakeasyLogo />
            </div>
          }
          timelineInfo={
            <div className={'timeline-info'}>
              <b className={'timeline-info__handle'}>Speakeasy Bot</b>
            </div>
          }
          className={cn(containerStyles())}
        >
          <span>Ok, you&apos;re all setup!</span>
        </TimelineContent>
      )}
      <ApiErrorMessage
        httpMethod={'put'}
        route={liteApiRoutes.lite_api_v1_account.path}
      />
    </Stack>
  )
}

const avatarStyles = css({
  width: 40,
  height: 40,
  borderRadius: '$2',
  backgroundColor: 'white',
  '> *': {
    width: 'inherit',
    height: 'inherit',
  },
})

const containerStyles = css({
  padding: '$1 $4 !important',

  '>:nth-child(2)': {
    maxWidth: 400,
  },

  '@hover': {
    '&:hover': {
      backgroundColor: 'rgba(200, 200, 200, 0.2)',
    },
  },
})

const blueButtonStyles = css({
  background: '#14abf5 !important',
})

const blueTextStyles = css({
  color: '#14abf5',
})

const checkIconStyles = css({
  background: '#14abf5',
  color: 'white',
  padding: '$1',
  borderRadius: '50%',
  marginLeft: '$2',
  fontSize: '11px',
  textAlign: 'center',
  verticalAlign: 'middle',
})
